import { ServerEvent } from "@/common/notification";
import {
	QueuesActions,
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { useAuth } from "@/hooks/authentication";
import { notificationListener } from "@/hooks/notification";
import { Queue } from "@/repository/models";
import { fetchMemberedQueues } from "@/repository/queryFns";
import { useCallback, useEffect } from "react";

export const useAgentQueuesManager = () => {
	const authContext = useAuth();
	const queueActions = useQueuesStore((actions: QueuesActions) => actions);
	const pendingQueueIds = useQueuesStore((state: QueuesState) => state.pendingQueueIds);

	useEffect(() => {
		if (!authContext.isLogged) {
			return;
		}

		fetchMemberedQueues()
			.then(queueActions.setQueues)
			.then(() => {
				queueActions.setIsDataLoaded(true);
			});
	}, [authContext.isLogged]);

	const refreshQueues = useCallback(
		(queueId?: string) => {
			fetchMemberedQueues().then((queues: Queue[]) => {
				if (queueId && typeof queueId === "string") {
					const queue = queues.find((x) => x.id == queueId);

					if (queue) {
						queueActions.update(queue);
					}
				} else {
					queueActions.setQueues(queues);
				}
			});
		},
		[queueActions.setQueues]
	);

	notificationListener(ServerEvent.UserMembershipChanged, (notifiedQueue: Queue) => {
		if (!pendingQueueIds.has(notifiedQueue.id)) {
			queueActions.update(notifiedQueue);
		}

		fetchMemberedQueues().then((queues: Queue[]) => {
			queueActions.setQueues(queues.map((q) => (q.id === notifiedQueue.id ? { ...q, ...notifiedQueue } : q)));
		});
	});

	notificationListener(
		ServerEvent.UserMembershipRemoved,
		({ id }: { id: string }) => {
			queueActions.remove(id);
		}
	);

	notificationListener(ServerEvent.UserPermisionChanged, refreshQueues);
	notificationListener(ServerEvent.QueueChanged, refreshQueues);
};
